import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api'
import { queryKeys } from '@/lib/query'
import { MOCK_USERS } from '@/mocks/directories'
import { USE_MOCKS } from '@/mocks/fixtures'
import type { CreatedUser, DirectoryUser, Grants, Office, Role } from '@/types/domain'

/* User management — Story 1.3.
 *
 * These endpoints live under /admin and are refused server-side for every role
 * but Super-admin (FR-56, AD-5). The client hides the section too, but hiding
 * is an affordance, not the enforcement: reaching the URL directly answers 403.
 *
 * There is no password field anywhere in this file, in either direction. The
 * server generates a credential and returns it exactly once — at creation or
 * reset — and stores only its Argon2id encoding, so no screen can offer to
 * show an existing one.
 */

/** The request body for creating or amending an account. */
export interface UserPayload {
  name: string
  email: string
  role: Role
  office: Office
  language: 'en' | 'ru'
  grants: Grants
}

export function useUsers() {
  return useQuery<DirectoryUser[]>({
    queryKey: queryKeys.directories.users(),
    queryFn: async () =>
      USE_MOCKS
        ? MOCK_USERS
        : (await api.get<{ items: DirectoryUser[] }>('/admin/users')).items,
  })
}

/** Invalidates the user list after any change.
 *
 * The session is invalidated alongside it because a Super-admin editing their
 * own record changes their own language or grants, and the header reads those
 * from the session query rather than from this list. */
function useInvalidateUsers() {
  const queryClient = useQueryClient()
  return () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.directories.users() })
    void queryClient.invalidateQueries({ queryKey: queryKeys.session })
  }
}

/** Creates an account. Resolves with the generated credential, which the caller
 *  must show once and cannot retrieve again. */
export function useCreateUser() {
  const invalidate = useInvalidateUsers()

  return useMutation<CreatedUser, Error, UserPayload>({
    mutationFn: (payload) => api.post<CreatedUser>('/admin/users', payload),
    onSuccess: invalidate,
  })
}

/** Amends an account.
 *
 * The whole record is submitted rather than a patch: user management is a form,
 * and a partial body would make "clear this grant" indistinguishable from
 * "leave it alone" — the bug that silently re-grants a permission somebody
 * just revoked. Email is absent because an address identifies an account. */
export function useUpdateUser() {
  const invalidate = useInvalidateUsers()

  return useMutation<DirectoryUser, Error, { id: string; payload: Omit<UserPayload, 'email'> }>({
    mutationFn: ({ id, payload }) => api.put<DirectoryUser>(`/admin/users/${id}`, payload),
    onSuccess: invalidate,
  })
}

/** Activates or deactivates an account.
 *
 * Deactivation ends every session that account holds server-side, so their next
 * request is refused rather than waiting for a token to expire (FR-57). */
export function useSetUserActive() {
  const invalidate = useInvalidateUsers()

  return useMutation<DirectoryUser, Error, { id: string; isActive: boolean }>({
    mutationFn: ({ id, isActive }) =>
      api.patch<DirectoryUser>(`/admin/users/${id}/active`, { isActive }),
    onSuccess: invalidate,
  })
}

/** Issues a new credential and invalidates every session the account holds. */
export function useResetUserPassword() {
  const invalidate = useInvalidateUsers()

  return useMutation<CreatedUser, Error, string>({
    mutationFn: (id) => api.post<CreatedUser>(`/admin/users/${id}/password`),
    onSuccess: invalidate,
  })
}
